import type { DetailCoverProps, DetailFooterProps, NotificationDetailProps } from './const';
import { Button } from '@wegic/ui'
import '@wegic/ui/dist/components/Button/index.css';;
import classNames from 'classnames';
import MarkdownViewer from '@/components/Markdown/MarkdownViewer';
import { collect } from '@/modules/Collect/helper';
import { formatDate, getValidFooterActions, hasNotificationMedia, isVideoCover } from '../helper';
import styles from './index.module.less';
import { markdownComponents } from './markdownComponents';

function DetailCover({ notification, variant }: DetailCoverProps) {
  const withMedia = hasNotificationMedia(notification.coverType, notification.coverUrl);
  const isVideo = isVideoCover(notification.coverType);
  const shellClass = classNames(styles['media-shell'], styles[`media-shell-${variant}`]);

  if (!withMedia) {
    return (
      <div className={shellClass}>
        <div className={styles['media-fallback']} />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className={shellClass}>
        <video
          className={styles.media}
          src={notification.coverUrl || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controls={false}
          disablePictureInPicture
        />
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <img className={styles.media} src={notification.coverUrl || undefined} alt="" />
    </div>
  );
}

function DetailFooter({
  footerActions,
  notification,
  activeTab,
  onAction,
}: DetailFooterProps) {
  const validActions = getValidFooterActions(footerActions);
  if (validActions.length === 0) {
    return null;
  }

  return (
    <div className={styles.footer}>
      <div className={styles['footer-mask']} />
      <div className={styles['footer-actions']}>
        {validActions.map(({ action, label }, index) => {
          const isPrimary = index === 0;
          const handleClick = () => {
            collect('notification_footer_action_click', {
              notification_id: notification.id,
              category: activeTab,
              action_type: action.type,
            });
            onAction(notification, action);
          };
          return (
            <Button
              key={`${action.type}-${index}`}
              type="primary"
               size="small"
              className={classNames(
                styles['footer-action'],
                isPrimary ? styles['footer-action-primary'] : styles['footer-action-secondary'],
              )}
              onClick={handleClick}
            >
              {`${label} →`}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function NotificationDetail({ notification, activeTab, onAction }: NotificationDetailProps) {
  const coverVariant = activeTab === 'update' ? 'featured' : 'compact';

  return (
    <div className={styles.detail}>
      <div className={styles.body}>
        <DetailCover notification={notification} variant={coverVariant} />
        <div className={styles['content-card']}>
          <div className={styles['title-row']}>
            <div className={styles.title}>{notification.title}</div>
            <div className={styles.date}>{formatDate(notification.publishedAt)}</div>
          </div>
          {notification.subtitle && <div className={styles.subtitle}>{notification.subtitle}</div>}
          <MarkdownViewer
            content={notification.content}
            className={styles.markdown}
            components={markdownComponents}
          />
        </div>
      </div>
      <DetailFooter
        footerActions={notification.footerActions}
        notification={notification}
        activeTab={activeTab}
        onAction={onAction}
      />
    </div>
  );
}

export default NotificationDetail;
